import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { inventoryService, Category, Item } from '../services/inventoryService';
import { useAuth } from '../context/AuthContext';
import './InventoryList.css';

const DEFAULT_STORAGE_LOCATIONS = ['Dachau', 'Indersdorf'];
const STORAGE_LOCATIONS_KEY = 'inventory-storage-locations';

export default function InventoryList() {
  const { isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState<Category[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>(searchParams.get('category') || '');
  const [selectedLocation, setSelectedLocation] = useState<string>(searchParams.get('location') || '');
  const [searchTerm, setSearchTerm] = useState('');
  const [customLocations, setCustomLocations] = useState<string[]>([]);
  const [showCreateItemForm, setShowCreateItemForm] = useState(false);
  const [createItemForm, setCreateItemForm] = useState({
    categoryId: searchParams.get('category') || '',
    name: '',
    quantity: 1,
    storageLocation: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Sync filters with URL params whenever they change
    const category = searchParams.get('category') || '';
    const location = searchParams.get('location') || '';
    setSelectedCategory(category);
    setSelectedLocation(location);
    setCreateItemForm((prev) => ({
      ...prev,
      categoryId: prev.categoryId || category,
    }));
  }, [searchParams]);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_LOCATIONS_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        setCustomLocations(parsed.filter((location) => typeof location === 'string' && location.trim()));
      }
    } catch (err) {
      console.warn('Konnte gespeicherte Standorte nicht laden', err);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [selectedCategory, selectedLocation]);

  const buildFilterParams = (category: string, location: string) => {
    const nextParams: Record<string, string> = {};
    if (category) {
      nextParams.category = category;
    }
    if (location) {
      nextParams.location = location;
    }
    return nextParams;
  };

  const getItemLocations = (item: Item) => {
    const directLocation = (item.data.storageLocation || item.data.standort || '').trim();
    const instanceLocations = Array.from(new Set((item.data.instances || [])
      .map((instance) => instance.storageLocation?.trim())
      .filter((location): location is string => Boolean(location))));

    if (instanceLocations.length > 0) {
      if (directLocation && directLocation !== 'Individuell' && !instanceLocations.includes(directLocation)) {
        return [...instanceLocations, directLocation];
      }
      return instanceLocations;
    }

    if (directLocation && directLocation !== 'Individuell') {
      return [directLocation];
    }

    return [];
  };

  const itemHasLocation = (item: Item, location: string) => {
    const target = location.trim();
    if (!target) return true;
    return getItemLocations(item).includes(target);
  };

  const getItemLocation = (item: Item) => {
    const locations = getItemLocations(item);
    if (locations.length === 1) return locations[0];
    if (locations.length > 1) return 'Mehrere Standorte';
    return '';
  };

  const locationOptions = useMemo(() => {
    const knownLocations = new Set<string>([...DEFAULT_STORAGE_LOCATIONS, ...customLocations]);
    items.forEach((item) => {
      getItemLocations(item).forEach((location) => {
        knownLocations.add(location);
      });
    });
    if (selectedLocation) {
      knownLocations.add(selectedLocation);
    }
    if (createItemForm.storageLocation) {
      knownLocations.add(createItemForm.storageLocation);
    }
    return Array.from(knownLocations).sort((a, b) => a.localeCompare(b, 'de'));
  }, [items, selectedLocation, createItemForm.storageLocation, customLocations]);

  const saveCustomLocations = (locations: string[]) => {
    const normalized = Array.from(new Set(locations.map((location) => location.trim()).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b, 'de'));
    setCustomLocations(normalized);
    localStorage.setItem(STORAGE_LOCATIONS_KEY, JSON.stringify(normalized));
  };

  const handleAddLocation = () => {
    const input = window.prompt('Neuen Standort eingeben:');
    if (!input) return;
    const newLocation = input.trim();
    if (!newLocation) return;

    const exists = locationOptions.some((location) => location.toLowerCase() === newLocation.toLowerCase());
    if (!exists) {
      saveCustomLocations([...customLocations, newLocation]);
    }

    setCreateItemForm((prev) => ({ ...prev, storageLocation: newLocation }));
  };

  const loadCategories = async () => {
    try {
      const data = await inventoryService.getCategories();
      setCategories(data);
    } catch (err) {
      setError('Fehler beim Laden der Kategorien');
    }
  };

  const loadItems = async () => {
    setLoading(true);
    try {
      const data = await inventoryService.getItems(selectedCategory || undefined, selectedLocation || undefined);
      setItems(data);
    } catch (err) {
      setError('Fehler beim Laden der Items');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      loadItems();
      return;
    }

    setLoading(true);
    try {
      const data = await inventoryService.search(searchTerm);
      // Combine items from search results
      const allItems = [...data.items];
      // Add items from compartments that matched
      const compartmentItems = data.compartments.map((c: any) => c.item).filter((i: any) => i);
      allItems.push(...compartmentItems);
      
      // Remove duplicates
      const uniqueItems = Array.from(new Map(allItems.map(item => [item.id, item])).values());
      const locationFilteredItems = selectedLocation
        ? uniqueItems.filter((item) => itemHasLocation(item, selectedLocation))
        : uniqueItems;
      setItems(locationFilteredItems);
    } catch (err) {
      setError('Fehler beim Suchen');
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = items;

  const groupedItems = useMemo(() => {
    const groups = new Map<string, { categoryId: string; categoryName: string; items: Item[] }>();

    categories.forEach((category) => {
      groups.set(category.id, {
        categoryId: category.id,
        categoryName: category.name,
        items: [],
      });
    });

    const uncategorizedKey = '__uncategorized__';

    filteredItems.forEach((item) => {
      const categoryId = item.category?.id;
      const categoryName = item.category?.name || 'Ohne Kategorie';

      if (categoryId && groups.has(categoryId)) {
        groups.get(categoryId)!.items.push(item);
        return;
      }

      if (!groups.has(uncategorizedKey)) {
        groups.set(uncategorizedKey, {
          categoryId: uncategorizedKey,
          categoryName,
          items: [],
        });
      }
      groups.get(uncategorizedKey)!.items.push(item);
    });

    return Array.from(groups.values()).filter((group) => group.items.length > 0);
  }, [categories, filteredItems]);

  const selectedCategoryName = useMemo(() => {
    if (!selectedCategory) {
      return '';
    }
    const category = categories.find((cat) => cat.id === selectedCategory);
    return category?.name || 'Kategorie';
  }, [categories, selectedCategory]);

  const handleCreateStorageItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createItemForm.categoryId) {
      setError('Bitte eine Kategorie wählen');
      return;
    }
    if (!createItemForm.name.trim()) {
      setError('Bitte einen Namen für das Lagerobjekt eingeben');
      return;
    }

    try {
      setError('');
      await inventoryService.createItem({
        categoryId: createItemForm.categoryId,
        name: createItemForm.name.trim(),
        quantity: createItemForm.quantity,
        storageLocation: createItemForm.storageLocation.trim() || undefined,
      });

      setShowCreateItemForm(false);
      setCreateItemForm((prev) => ({ ...prev, name: '', quantity: 1, storageLocation: '' }));

      if (selectedCategory !== createItemForm.categoryId) {
        setSelectedCategory(createItemForm.categoryId);
        setSearchParams(buildFilterParams(createItemForm.categoryId, selectedLocation));
      } else {
        await loadItems();
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Fehler beim Erstellen des Lagerobjekts');
    }
  };

  return (
    <div className="inventory-list">
      <div className="page-header">
        <h1>Inventar</h1>
        {isAdmin && (
          <button
            className="btn-add-storage"
            onClick={() => {
              setShowCreateItemForm((prev) => !prev);
              setError('');
            }}
          >
            {showCreateItemForm ? 'Abbrechen' : 'Lagerobjekt hinzufügen'}
          </button>
        )}
      </div>

      {isAdmin && showCreateItemForm && (
        <form className="create-storage-form" onSubmit={handleCreateStorageItem}>
          <div className="form-field">
            <label>Kategorie</label>
            <select
              value={createItemForm.categoryId}
              onChange={(e) => setCreateItemForm((prev) => ({ ...prev, categoryId: e.target.value }))}
              required
            >
              <option value="">Kategorie wählen</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div className="form-field">
            <label>Name</label>
            <input
              type="text"
              value={createItemForm.name}
              onChange={(e) => setCreateItemForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="z.B. SAN-Rucksack Neu"
              required
            />
          </div>

          <div className="form-field qty-field">
            <label>Anzahl</label>
            <input
              type="number"
              min="1"
              value={createItemForm.quantity}
              onChange={(e) => setCreateItemForm((prev) => ({ ...prev, quantity: Math.max(1, parseInt(e.target.value) || 1) }))}
            />
          </div>

          <div className="form-field">
            <label>Standort</label>
            <select
              value={createItemForm.storageLocation}
              onChange={(e) => setCreateItemForm((prev) => ({ ...prev, storageLocation: e.target.value }))}
            >
              <option value="">Standort wählen</option>
              {locationOptions.map((location) => (
                <option key={location} value={location}>{location}</option>
              ))}
            </select>
          </div>

          <button type="submit" className="btn-create-storage">Anlegen</button>
        </form>
      )}

      <div className="filters">
        <div className="filter-group">
          <label>Kategorie:</label>
          <select 
            value={selectedCategory} 
            onChange={(e) => {
              const newCategory = e.target.value;
              setSelectedCategory(newCategory);
              setSearchParams(buildFilterParams(newCategory, selectedLocation));
            }}
          >
            <option value="">Alle Kategorien</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Standort:</label>
          <select
            value={selectedLocation}
            onChange={(e) => {
              const newLocation = e.target.value;
              setSelectedLocation(newLocation);
              setSearchParams(buildFilterParams(selectedCategory, newLocation));
            }}
          >
            <option value="">Alle Standorte</option>
            {locationOptions.map((location) => (
              <option key={location} value={location}>{location}</option>
            ))}
          </select>
          {isAdmin && (
            <button type="button" className="btn-add-location" onClick={handleAddLocation}>
              Standort hinzufügen
            </button>
          )}
        </div>

        <div className="search-group">
          <input
            type="text"
            placeholder="Kategorien durchsuchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button onClick={handleSearch} className="btn-search">Suchen</button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Lädt...</div>
      ) : filteredItems.length === 0 ? (
        <p className="no-items">Keine Items gefunden</p>
      ) : !selectedCategory ? (
        <div className="category-sections">
          {groupedItems.map((group) => (
            <section key={group.categoryId} className="category-section">
              <h2 className="category-subheadline">{group.categoryName}</h2>
              <div className="items-grid">
                {group.items.map((item) => (
                  <Link
                    key={item.id}
                    to={`/inventory/${item.id}`}
                    className="item-card"
                  >
                    <h3>
                      {item.data.bezeichnung || item.data.id}
                      {item.data.quantity && item.data.quantity > 1 && (
                        <span className="quantity-badge"> ({item.data.quantity}x)</span>
                      )}
                    </h3>
                    <p className="category-badge">{item.category?.name}</p>
                    {getItemLocation(item) && <p className="location">{getItemLocation(item)}</p>}
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <section className="category-section">
          <h2 className="category-subheadline">{selectedCategoryName}</h2>
          <div className="items-grid">
            {filteredItems.map(item => (
              <Link 
                key={item.id} 
                to={`/inventory/${item.id}?category=${selectedCategory}`} 
                className="item-card"
              >
                <h3>
                  {item.data.bezeichnung || item.data.id}
                  {item.data.quantity && item.data.quantity > 1 && (
                    <span className="quantity-badge"> ({item.data.quantity}x)</span>
                  )}
                </h3>
                <p className="category-badge">{item.category?.name}</p>
                {getItemLocation(item) && <p className="location">{getItemLocation(item)}</p>}
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
